import avatarImage from "../assets/images/Avatar.png";

/**
 * Fallback avatar for users with no `pictureProfile`.
 *
 * This was written out as a full GCS URL in 20 places across 11 components, which
 * made it the one image URL the database rewrite could not reach — it lives in
 * source, not in Mongo. Hoisting it here means the next person to change the
 * default avatar changes it once.
 *
 * A static import rather than the `image.games-on.net/profiles/Avatar.png` URL:
 * it ships from `/_next/static/media` with a content hash and an immutable
 * Cache-Control instead of costing a media Worker request per avatar-less author
 * on a first visit. Not `public/` — files without an explicit rule there get
 * `max-age=0, must-revalidate`. Every consumer passes this to `<Image src>`,
 * which takes `StaticImageData` directly (the pattern HeroBanner and Trending
 * already use); read `.src` if you ever need the URL as a string.
 */
export const DEFAULT_AVATAR = avatarImage;

export const getBackendOrigin = (): string => {
  const baseUrl = process.env.BACKEND_BASE_URL;
  if (!baseUrl || typeof baseUrl !== "string") return "";

  try {
    return new URL(baseUrl).origin;
  } catch {
    return "";
  }
};

export const resolveImageSrc = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const src = value.trim();

  if (!src || src === "null" || src === "undefined") return "";

  if (
    src.startsWith("data:") ||
    src.startsWith("blob:") ||
    src.startsWith("https://")
  ) {
    return src;
  }

  if (src.startsWith("http://")) {
    // Avoid mixed-content blocking when app is served over HTTPS.
    return `https://${src.slice("http://".length)}`;
  }

  if (src.startsWith("//")) {
    return `https:${src}`;
  }

  const backendOrigin = getBackendOrigin();
  if (src.startsWith("/")) {
    return backendOrigin ? `${backendOrigin}${src}` : src;
  }

  if (/^[^/\s]+\.[^/\s]+\/.+/.test(src)) {
    return `https://${src}`;
  }

  return backendOrigin
    ? `${backendOrigin}/${src.replace(/^\/+/, "")}`
    : src;
};

export const toImageUrl = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof (value as { src?: unknown }).src === "string") {
    return (value as { src: string }).src;
  }
  return "";
};

/**
 * Ordered list of URLs to try for one image value, first to last.
 *
 * There is only ever one candidate now. It used to expand GCS URLs into
 * percent-decoded and percent-encoded variants of the object key, because
 * `file.publicUrl()` encoded `/` as `%2F` inconsistently and only one spelling
 * would resolve. R2 is served through `image.games-on.net` under a single key
 * layout, so the fallbacks have nothing left to fall back to.
 *
 * Kept as an array so the components that render a candidate list and advance on
 * `onError` keep working unchanged.
 */
export const getImageSourceCandidates = (value: unknown): string[] => {
  const resolved = resolveImageSrc(value);
  return resolved ? [resolved] : [];
};
